import React from "react";

import { css, cx } from "@emotion/css";
import { Group } from "@visx/group";
import { Line } from "@visx/shape";

import { splitInsulinData } from "@/business/InsulinLogic.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { DayData } from "@/models/CGMDailyGraphData.ts";
import { Insulin } from "@/models/diabetes/InsulinModel.ts";
import { InsulinDataDisplayType } from "@/models/DiabetesDisplayModeModel.ts";
import { InsulinBasalRate } from "@/models/InsulinBasalRate.ts";
import { ClosedLoopPumpModeChangeEvent, ClosedLoopUserModeChangeEvent, PumpEvent } from "@/models/InsulinPumpEvents.ts";
import { useTheme, Theme } from "@/theme/CreateTheme.ts";
import { BolusInsulinGraph } from "@/uiKit/molecules/graphs/InsulinGraph/BolusInsulinGraph.tsx";
import { ClosedLoopModeGraph } from "@/uiKit/molecules/graphs/InsulinGraph/ClosedLoopModeGraph.tsx";
import { ContinuousInsulinGraph } from "@/uiKit/molecules/graphs/InsulinGraph/ContinuousInsulinGraph.tsx";
import { DiscreteInsulinPoints } from "@/uiKit/molecules/graphs/InsulinGraph/DiscreteInsulinPoints.tsx";
import {
  ContinuousInsulinLegend,
  DiscreteInsulinLegend,
} from "@/uiKit/molecules/graphs/InsulinGraph/InsulinLegend.tsx";
import { SvgCoords, SvgLayout, SvgLegend } from "@/utils/graphUtils.ts";

export type InsulinGraphProps = {
  date: string;
  insulin: Insulin[];
  insulinBasalRates: DayData<InsulinBasalRate>;
  pumpEvents: PumpEvent[];
  closedLoopEvents: {
    userMode: DayData<ClosedLoopUserModeChangeEvent>;
    pumpMode: DayData<ClosedLoopPumpModeChangeEvent>;
  };
  insulinDisplayMode: InsulinDataDisplayType;
  className?: string;
  legend?: SvgLegend;
} & SvgLayout;

type InsulinGraphLayout = {
  eventArea: SvgLayout;
  fast: SvgLayout;
  fastLegend: SvgLayout;
  slow: SvgLayout;
  slowLegend: SvgLayout;
  bolus: SvgLayout;
  bolusLegend: SvgLayout;
  basal: SvgLayout;
  basalLegend: SvgLayout;
  closedLoop: SvgLayout;
  closedLoopLegend: SvgLayout;
  bolusBottomLine: SvgCoords;
  basalBottomLine: SvgCoords;
  closedLoopBottomLine: SvgCoords;
  verticalPadding: number;
};

const InsulinGraph_: React.FC<InsulinGraphProps> = ({
  date,
  insulin,
  pumpEvents,
  closedLoopEvents,
  insulinBasalRates,
  insulinDisplayMode,
  legend,
  className,
  ...dim
}) => {
  const styles = useStyles(makeStyles);
  const theme = useTheme();
  const data = splitInsulinData(insulin);

  const layout = getLayout(dim, insulinDisplayMode, legend);
  const legendContentWidth = (legend?.legendWidth ?? 0) - (legend?.legendPadding.x ?? 0) * 2;

  if (insulinDisplayMode === "discrete") {
    return (
      <Group className={cx(styles.container, className)} {...dim}>
        <DiscreteInsulinLegend
          enabled={!!legend}
          legendContentWidth={legendContentWidth}
          fastLegend={layout.fastLegend}
          slowLegend={layout.slowLegend}
        />

        <DiscreteInsulinPoints {...layout.fast} date={date} discrete={data.bolus} color={theme.palette.insulin.bolus} />
        <Line {...layout.bolusBottomLine} className={styles.line} />
        <DiscreteInsulinPoints
          {...layout.slow}
          date={date}
          discrete={data.discrete}
          color={theme.palette.insulin.basal}
        />
        <Line {...layout.basalBottomLine} className={styles.line} />
      </Group>
    );
  }
  return (
    <Group className={cx(styles.container, className)} {...dim}>
      <Line {...layout.bolusBottomLine} className={styles.line} />
      <Line {...layout.basalBottomLine} className={styles.line} />
      {insulinDisplayMode == "closed_loop" && <Line {...layout.closedLoopBottomLine} className={styles.line} />}
      <ContinuousInsulinLegend
        enabled={!!legend}
        legendContentWidth={legendContentWidth}
        bolusLegend={layout.bolusLegend}
        basalLegend={layout.basalLegend}
        closedLoopLegend={layout.closedLoopLegend}
        hasClosedLoopData={insulinDisplayMode == "closed_loop"}
      />
      <BolusInsulinGraph {...layout.bolus} date={date} bolus={data.bolus} paddingTop={layout.verticalPadding} />
      <ContinuousInsulinGraph
        {...layout.basal}
        date={date}
        insulinBasalRates={insulinBasalRates}
        discrete={data.discrete}
        pumpEvents={pumpEvents}
        paddingTop={layout.verticalPadding}
      />
      <ClosedLoopModeGraph
        {...layout.closedLoop}
        date={date}
        pumpModes={closedLoopEvents.pumpMode}
        userModes={closedLoopEvents.userMode}
        paddingTop={layout.verticalPadding * 0.7}
      />
    </Group>
  );
};

/**
 * Memoized {@link InsulinGraph_} component
 * Pure component, no need to re-render if props are the same
 * @see _InsulinGraph
 *
 */
export const InsulinGraph = React.memo(InsulinGraph_);

const getLayout = (
  dim: SvgLayout,
  insulinDisplayMode: InsulinDataDisplayType,
  legend: SvgLegend | undefined,
): InsulinGraphLayout => {
  const hasClosedLoopData = insulinDisplayMode === "closed_loop";
  const fastHeight = dim.height / 2;
  const slowHeight = dim.height / 2;
  const bolusHeight = dim.height * (hasClosedLoopData ? 0.5 : 0.6);
  const basalHeight = dim.height * (hasClosedLoopData ? 0.2 : 0.3);
  const closedLoopHeight = dim.height * (hasClosedLoopData ? 0.2 : 0);
  const graphHorizontalPadding = dim.left;
  const left = legend ? legend.legendWidth : 0;
  const width = legend ? dim.width - legend.legendWidth : dim.width;
  const contentVerticalPadding = dim.height * 0.05;
  const bolusBottom = insulinDisplayMode === "discrete" ? fastHeight : bolusHeight;
  const basalBottom = insulinDisplayMode === "discrete" ? slowHeight + bolusBottom : basalHeight + bolusBottom;
  const closedLoopBottom = hasClosedLoopData ? basalBottom + closedLoopHeight : basalBottom;
  const legendPadding = legend?.legendPadding ?? { x: 0, y: 0 };
  const legendWidth = legend?.legendWidth ?? 0;

  return {
    eventArea: {
      top: 0,
      left,
      width,
      height: dim.height,
    },
    fast: {
      top: 0,
      left,
      width,
      height: fastHeight,
    },
    fastLegend: {
      left: legendPadding.x,
      top: legendPadding.y,
      width: legendWidth - legendPadding.x * 2,
      height: fastHeight - legendPadding.y * 2,
    },
    slow: {
      top: fastHeight,
      left,
      width,
      height: slowHeight,
    },
    slowLegend: {
      left: legendPadding.x,
      top: fastHeight + legendPadding.y,
      width: legendWidth - legendPadding.x * 2,
      height: slowHeight - legendPadding.y * 2,
    },
    bolus: {
      top: 0,
      left,
      width,
      height: bolusHeight,
    },
    bolusLegend: {
      left: legendPadding.x,
      top: legendPadding.y,
      width: legendWidth - legendPadding.x * 2,
      height: bolusHeight - legendPadding.y * 2,
    },
    basal: {
      top: bolusHeight,
      left,
      width,
      height: basalHeight,
    },
    basalLegend: {
      left: legendPadding.x,
      top: bolusHeight + legendPadding.y,
      width: legendWidth - legendPadding.x * 2,
      height: basalHeight - legendPadding.y * 2,
    },
    closedLoop: {
      top: bolusHeight + basalHeight,
      left,
      width,
      height: closedLoopHeight,
    },
    closedLoopLegend: {
      left: legendPadding.x,
      top: bolusHeight + basalHeight + legendPadding.y,
      width: legendWidth - legendPadding.x * 2,
      height: closedLoopHeight - legendPadding.y * 2,
    },
    bolusBottomLine: {
      from: {
        x: 0,
        y: bolusBottom,
      },
      to: {
        x: dim.width + graphHorizontalPadding,
        y: bolusBottom,
      },
    },
    basalBottomLine: {
      from: {
        x: 0,
        y: basalBottom,
      },
      to: {
        x: dim.width + graphHorizontalPadding,
        y: basalBottom,
      },
    },
    closedLoopBottomLine: {
      from: {
        x: 0,
        y: closedLoopBottom,
      },
      to: {
        x: dim.width + graphHorizontalPadding,
        y: closedLoopBottom,
      },
    },
    verticalPadding: contentVerticalPadding,
  };
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  line: css({
    stroke: theme.palette.grey[100],
    strokeWidth: 1,
  }),
});
